// ==============================|| ROLE UTILITIES ||============================== //

export const USER_ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  WAITER: 'waiter',
  CUSTOMER: 'customer'
} as const;

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];

// Role hierarchy (higher number = more permissions)
export const ROLE_HIERARCHY: Record<UserRole, number> = {
  [USER_ROLES.ADMIN]: 4,
  [USER_ROLES.MANAGER]: 3,
  [USER_ROLES.WAITER]: 2,
  [USER_ROLES.CUSTOMER]: 1
};

// Default redirect paths for each role
export const ROLE_DEFAULT_PATHS: Record<UserRole, string> = {
  [USER_ROLES.ADMIN]: '/admin',
  [USER_ROLES.MANAGER]: '/restaurant/workers',
  [USER_ROLES.WAITER]: '/waiter/profile',
  [USER_ROLES.CUSTOMER]: '/customer/my-reservation/active'
};

/**
 * Check if a user has a specific role
 */
export const hasRole = (userRole: string | undefined, requiredRole: UserRole): boolean => {
  return userRole === requiredRole;
};

/**
 * Check if a user has any of the specified roles
 */
export const hasAnyRole = (userRole: string | undefined, allowedRoles: UserRole[]): boolean => {
  if (!userRole) return false;
  return allowedRoles.includes(userRole as UserRole);
};

/**
 * Check if a user has a role with sufficient permissions (hierarchy-based)
 */
export const hasMinimumRole = (userRole: string | undefined, minimumRole: UserRole): boolean => {
  if (!userRole) return false;
  const userRoleLevel = ROLE_HIERARCHY[userRole as UserRole] || 0;
  const minimumLevel = ROLE_HIERARCHY[minimumRole];
  return userRoleLevel >= minimumLevel;
};

/**
 * Get the appropriate redirect path for a user role
 */
export const getRoleRedirectPath = (userRole: string | undefined): string => {
  if (!userRole) return '/auth/login';
  return ROLE_DEFAULT_PATHS[userRole as UserRole] || '/auth/login';
};

/**
 * Get role display name for UI
 */
export const getRoleDisplayName = (role: string): string => {
  const roleMap: Record<string, string> = {
    [USER_ROLES.ADMIN]: 'Administrator',
    [USER_ROLES.MANAGER]: 'Restaurant Manager',
    [USER_ROLES.WAITER]: 'Waiter',
    [USER_ROLES.CUSTOMER]: 'Customer'
  };
  return roleMap[role] || role;
};
